// @react
import React, { ReactElement } from 'react'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Box from '@bit/atd.web.box'
import Divider from '@bit/atd.web.divider'
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import colors from '@bit/atd.web.colors/dist/colors'
import strings from 'lib/constants/strings'

type PropsType = {
  handleMemberOnClick?: (
    tier?: 'professional' | 'professional-plus' | 'enterprise',
    type?:
      | 'join-now'
      | 'learn-more'
      | 'select-student'
      | 'select-senior'
      | 'select-professional',
  ) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  cardDivider: {
    position: 'relative',
    padding: '24px 0',
  },
  cardLabel: {
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: '16px',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  cardTitle: {
    color: '#2E2D31',
    fontSize: '24px',
    lineHeight: '32px',
    marginBottom: '24px',
    [theme.breakpoints.up('sm')]: {
      marginTop: '24px',
    },
  },
  cardPrice: {
    color: '#2E2D31',
    fontSize: '48px',
    fontWeight: 600,
    lineHeight: '38px',
    marginBottom: '32px',
    '& span': {
      color: '#424140',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '16px',
    },
  },
  cardActionButtons: {
    '& button:last-child': {
      marginLeft: '8px',
    },
  },
  cardTitleList: {
    color: '#424140',
    fontWeight: 500,
    lineHeight: '22px',
    marginBottom: '16px',
  },
  cardList: {
    color: '#424140',
    flex: '1 0 auto',
    padding: 0,
    margin: 0,
    '& li': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
      '&:last-child': {
        marginBottom: '0',
      },
    },
    '& svg': {
      marginRight: '8px',
      display: 'inline-block',
      lineHeight: 1,
      flexShrink: 0,
      maxWidth: 'initial',
    },
    '& span': {
      fontWeight: 500,
    },
  },
  cardTitleRates: {
    color: '#2E2D31',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: '16px',
  },
  cardRatesPlan: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#2E2D31',
  },
  cardRatesPrice: {
    color: '#2E2D31',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '34px',
    marginBottom: '8px',
    '& span': {
      color: '#424140',
      display: 'block',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
    },
  },
  cardMoreCaption: {
    paddingLeft: '35px',
    color: 'grey',
  },
}))

/**
 *
 * @param handleMemberOnClick
 * @constructor
 */
const Index = ({ handleMemberOnClick }: PropsType): ReactElement => {
  const classes = useStyles()

  const handleOnClick = (type?: any) => {
    if (handleMemberOnClick) handleMemberOnClick('professional', type)
  }

  return (
    <>
      <Box
        classNames={{
          body: classes.body,
        }}
      >
        <Typography className={classes.cardTitle} component="h3">
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.TITLE}
        </Typography>
        <Typography className={classes.cardPrice} component="p">
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.PRICE}{' '}
          <span>{strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.PRICE_LABEL}</span>
        </Typography>
        <div className={classes.cardActionButtons}>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleOnClick('join-now')}
          >
            {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.BUTTON_ONE}
          </Button>
          <Button
            size="small"
            variant="tertiary"
            onClick={() => handleOnClick('learn-more')}
          >
            {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.BUTTON_TWO}
          </Button>
        </div>

        <div className={classes.cardDivider}>
          <Divider width="100%" color={colors.gray300} height="1px" />
        </div>

        <Typography className={classes.cardTitleList} component="p">
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.FEATURE_TEXT}
        </Typography>
        <ul className={classes.cardList}>
          <li>
            <IconCheckmark />
            <span>
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.FEATURES.FEATURE_MAG}
            </span>
          </li>
          <li>
            <IconCheckmark />
            <span>
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.FEATURES.FEATURE_LIB}
            </span>
          </li>
          <li>
            <IconCheckmark />
            <span>
              {
                strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.FEATURES
                  .FEATURE_GUIDE
              }
            </span>
          </li>
          <li>
            <span className={classes.cardMoreCaption}>
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.TEXT}
            </span>
          </li>
        </ul>

        <div className={classes.cardDivider}>
          <Divider width="100%" color={colors.gray300} height="1px" />
        </div>

        <Typography className={classes.cardTitleRates} component="p">
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATE_TITLE}
        </Typography>

        <Grid container alignItems={'flex-end'}>
          <Grid item xs={4} style={{ textAlign: 'center' }}>
            <Typography className={classes.cardRatesPlan} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.STUDENTS.TITLE}
            </Typography>
            <Typography className={classes.cardRatesPrice} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.STUDENTS.PRICE}{' '}
              <span>
                {
                  strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.STUDENTS
                    .PRICE_LABEL
                }
              </span>
            </Typography>
            <Button
              size="small"
              variant="secondary"
              onClick={() => handleOnClick('select-student')}
            >
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.STUDENTS.BUTTON}
            </Button>
          </Grid>

          <Grid item xs={4} style={{ textAlign: 'center' }}>
            <Typography className={classes.cardRatesPlan} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.SENIOR.TITLE}
            </Typography>
            <Typography className={classes.cardRatesPrice} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.SENIOR.PRICE}{' '}
              <span>
                {
                  strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.SENIOR
                    .PRICE_LABEL
                }
              </span>
            </Typography>
            <Button
              size="small"
              variant="secondary"
              onClick={() => handleOnClick('select-senior')}
            >
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.SENIOR.BUTTON}
            </Button>
          </Grid>

          <Grid item xs={4} style={{ textAlign: 'center' }}>
            <Typography className={classes.cardRatesPlan} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.YOUNG_PRO.TITLE}
            </Typography>
            <Typography className={classes.cardRatesPrice} component="p">
              {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.YOUNG_PRO.PRICE}{' '}
              <span>
                {
                  strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.YOUNG_PRO
                    .PRICE_LABEL
                }
              </span>
            </Typography>
            <Button
              size="small"
              variant="secondary"
              onClick={() => handleOnClick('select-professional')}
            >
              {
                strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL.RATES.YOUNG_PRO
                  .BUTTON
              }
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default Index

function IconCheckmark() {
  return (
    <svg width={24} height={24} fill="none">
      <circle cx={12} cy={12} r={11} stroke="#424140" strokeWidth={2} />
      <path
        d="M6.75 12.815l3.652 3.196L17.25 8.25"
        stroke="#424140"
        strokeWidth={2}
        strokeMiterlimit={10}
      />
    </svg>
  )
}
